import { Flex, Icon, SimpleInput, SimpleRange, Tooltip, Typography } from '@aloft/toolkit-sui';

import { GuidedTourTooltip } from '../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { sliderAdjustment } from '../../../redux/features/actions/sliderAdjustment.ts';
import {
  selectImprovementsById,
  selectSubjectiveCharacteristicsByIdAndField,
} from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectFirstOrFewerCompID } from '../../../redux/features/selectors/selectFirstOrFewerCompID.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { RootState } from '../../../redux/types.ts';
import { ContentBlock } from '../ColumnComponents/ContentBlock.tsx';
import { getCostQualityRange } from '../modals/objectiveEdit/getCostQualityRange.tsx';
import { ImageAnalysisQuality } from './ImageAnalysis/ImageAnalysisQuality.tsx';

const STEP = 0.1;

export const QualitySlider = ({ id }: { id: string }) => {
  const dispatch = useAppDispatch();

  const field = 'craftsmanQuality';
  const propertyType = useAppSelector(
    (state: RootState) =>
      selectImprovementsById(state, id)?.propertyType ?? 'singleFamilyResidence',
  );
  const costQualityRange = getCostQualityRange(propertyType);
  const value =
    useAppSelector((state: RootState) =>
      selectSubjectiveCharacteristicsByIdAndField(state, id, field),
    ) ?? undefined;
  const secondOrFewerCompID = useAppSelector(selectFirstOrFewerCompID);

  const hasCostQualityError = value === undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(sliderAdjustment({ id, field, newValue: Number(event.target.value) }));
  };

  const handleComboChange = (inputValue: string) => {
    dispatch(sliderAdjustment({ id, field, newValue: Number(inputValue) }));
  };

  return (
    <ContentBlock>
      <GuidedTourTooltip
        text={[
          'Using your comps as reference, tweak the cost quality and effective age of the subject.',
          "The subject's market value is the site value + the depreciated cost of the improvements. As you edit the subject's site value, quality and effective age, you are determining the market value of the subject and completing the subject's cost approach.",
        ]}
        tourID="analysisTour"
        tourIndex={9}
        displayCondition={id === 'subject'}
        placement="top"
      >
        <Flex flexDirection="row" alignItems="space-around" justifyContent="space-between">
          <Flex alignItems="baseline" gap={1}>
            <Flex alignItems="center" gap={0.5}>
              <Typography variant="h4">Cost Quality</Typography>
              {hasCostQualityError && <span style={{ color: 'red' }}>*</span>}
            </Flex>
            {propertyType === 'singleFamilyResidence' && (
              <ImageAnalysisQuality id={id} step={STEP} />
            )}
          </Flex>
          <Flex alignItems="baseline">
            <Tooltip
              label={
                <Flex flexDirection="column">
                  <Typography>Craftsman Quality</Typography>
                  {costQualityRange.labels.map((label, idx) => (
                    <Typography key={idx}>{label}</Typography>
                  ))}
                </Flex>
              }
            >
              <Flex flexDirection="row" gap={1} alignItems="center">
                <Typography variant="body2">
                  {value !== undefined ? costQualityRange.getLabel(value) : '—'}
                </Typography>
                <Icon icon="information" size="md" />
              </Flex>
            </Tooltip>
          </Flex>
        </Flex>
        <GuidedTourTooltip
          text="Next, use the photos of the property and the Quality Guide to estimate quality for each property."
          tourID="analysisTour"
          tourIndex={4}
          displayCondition={id === secondOrFewerCompID}
          placement="top"
        >
          <Flex gap="1rem" width="100%" justifyContent="space-between" alignItems="center">
            <SimpleRange
              value={value?.toString() ?? ''}
              min={costQualityRange.min}
              max={costQualityRange.max}
              step={STEP}
              onChange={handleChange}
            />
            <Flex width="4rem" flexDirection="column">
              <SimpleInput
                type="number"
                precision={1}
                min={costQualityRange.min}
                max={costQualityRange.max}
                value={value?.toString() ?? ''}
                onChange={(e) => handleComboChange(e.target.value)}
                width="100%"
                size="sm"
                invalid={hasCostQualityError}
                errorMessage={hasCostQualityError ? 'Required' : undefined}
              />
            </Flex>
          </Flex>
        </GuidedTourTooltip>
      </GuidedTourTooltip>
    </ContentBlock>
  );
};
